import { useState, useRef } from "react";
import Layout from "@/components/Layout";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Sparkles, Upload, Trash2, FileText, TrendingUp, FileDown, Calculator } from "lucide-react";
import { toast } from "sonner";
import { axiosInstance } from "@/App";

const brl = (n) =>
  Number(n || 0).toLocaleString("pt-BR", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const GC = "Geração Compartilhada";
const AC = "Autoconsumo Remoto";
const isAuto = (t) => (t || "").startsWith("Autoconsumo");
const suggestedDiscount = (it, type) => {
  const v = isAuto(type) ? it.suggested_autoconsumo_discount : it.suggested_discount;
  return v != null && v !== 0 ? String(v) : it.discount_percentage;
};

const STEP_UPLOAD = 1;
const STEP_REVIEW = 2;
const STEP_RESULTS = 3;

export default function BulkSimulator({ user, onLogout }) {
  const [step, setStep] = useState(STEP_UPLOAD);
  const [files, setFiles] = useState([]);
  const [extracting, setExtracting] = useState(false);
  const [simulating, setSimulating] = useState(false);

  // Etapa 2 (revisão) — cada item deriva de uma fatura extraída
  const [items, setItems] = useState([]);   // dados editáveis
  const [extractErrors, setExtractErrors] = useState([]);

  // Etapa 3 (resultados persistidos com simulation_id)
  const [results, setResults] = useState([]);
  const [simErrors, setSimErrors] = useState([]);
  const [summary, setSummary] = useState(null);
  const fileInputRef = useRef(null);

  const isAdmin = user?.role === "admin";
  if (!isAdmin) {
    return (
      <Layout user={user} onLogout={onLogout}>
        <div className="p-8 text-center text-slate-600">Acesso restrito a administradores.</div>
      </Layout>
    );
  }

  const resetAll = () => {
    setStep(STEP_UPLOAD);
    setFiles([]);
    setItems([]);
    setExtractErrors([]);
    setResults([]);
    setSimErrors([]);
    setSummary(null);
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const handleAddFiles = (e) => {
    const picked = Array.from(e.target.files || []);
    const ok = ["application/pdf", "image/jpeg", "image/jpg", "image/png", "image/webp"];
    const filtered = picked.filter((f) => {
      if (!ok.includes(f.type)) { toast.warning(`Ignorado (formato inválido): ${f.name}`); return false; }
      if (f.size > 15 * 1024 * 1024) { toast.warning(`Ignorado (>15MB): ${f.name}`); return false; }
      return true;
    });
    setFiles((prev) => [...prev, ...filtered].slice(0, 30));
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const removeFile = (idx) => setFiles((prev) => prev.filter((_, i) => i !== idx));

  const handleExtract = async () => {
    if (files.length === 0) { toast.error("Adicione pelo menos 1 fatura."); return; }
    setExtracting(true);
    const toastId = toast.loading(`Lendo ${files.length} fatura(s) com IA…`);
    try {
      const fd = new FormData();
      files.forEach((f) => fd.append("files", f));
      const resp = await axiosInstance.post("/simulations/bulk-extract", fd, {
        headers: { "Content-Type": "multipart/form-data" },
        timeout: 300000,
      });
      const d = resp?.data || {};
      const list = (d.results || []).map((r) => ({
        ...r,
        // Campos editáveis do formulário
        discount_percentage: r.suggested_discount != null ? String(r.suggested_discount) : "",
        partner_commission: "0",
        compensation_type: GC,
        // Numéricos como string para editar
        consumption: r.consumption != null ? String(r.consumption) : "",
        current_tariff: r.current_tariff != null ? String(r.current_tariff) : "",
        total_bill: r.total_bill != null ? String(r.total_bill) : "",
        icms: r.icms != null ? String(r.icms) : "",
        pis: r.pis != null ? String(r.pis) : "",
        cofins: r.cofins != null ? String(r.cofins) : "",
        public_lighting: r.public_lighting != null ? String(r.public_lighting) : "",
        others: r.others != null ? String(r.others) : "",
      }));
      setItems(list);
      setExtractErrors(d.errors || []);
      setStep(STEP_REVIEW);
      toast.success(`${list.length} fatura(s) lidas${(d.errors || []).length ? ` · ${(d.errors || []).length} erro(s)` : ""}`, { id: toastId });
    } catch (err) {
      toast.error(err?.response?.data?.detail || err?.message || "Erro ao ler faturas", { id: toastId });
    } finally {
      setExtracting(false);
    }
  };

  const updateItem = (idx, field, value) => {
    setItems((prev) => prev.map((it, i) => (i === idx ? { ...it, [field]: value } : it)));
  };

  const setModality = (idx, type) => {
    setItems((prev) => prev.map((it, i) =>
      i === idx ? { ...it, compensation_type: type, discount_percentage: suggestedDiscount(it, type) } : it));
  };

  const applyModalityToAll = (type) => {
    setItems((prev) => prev.map((it) => ({ ...it, compensation_type: type, discount_percentage: suggestedDiscount(it, type) })));
    toast.success(`Modalidade "${type}" aplicada a todas as faturas`);
  };

  const applyDiscountToAll = () => {
    const v = prompt("Aplicar qual % de desconto para TODAS as faturas?", "15");
    if (v == null) return;
    const num = parseFloat(String(v).replace(",", "."));
    if (isNaN(num)) { toast.error("Valor inválido"); return; }
    setItems((prev) => prev.map((it) => ({ ...it, discount_percentage: String(num) })));
    toast.success(`${num}% aplicado a todas as faturas`);
  };

  const handleSimulate = async () => {
    // Valida descontos
    const missing = items.findIndex((it) => !it.discount_percentage || isNaN(parseFloat(String(it.discount_percentage).replace(",", "."))));
    if (missing >= 0) {
      toast.error(`Informe o desconto na linha ${missing + 1}`);
      return;
    }
    setSimulating(true);
    const toastId = toast.loading("Gerando simulações…");
    try {
      const payload = {
        items: items.map((it) => ({
          filename: it.filename,
          cooperative_name: it.cooperative_name || "Cooperado",
          installation_number: it.installation_number || "",
          distributor: it.distributor || "",
          compensation_type: it.compensation_type || "Geração Compartilhada",
          phone: it.phone || "",
          consumption: parseFloat(String(it.consumption).replace(",", ".")) || 0,
          current_tariff: parseFloat(String(it.current_tariff).replace(",", ".")) || 0,
          discount_percentage: parseFloat(String(it.discount_percentage).replace(",", ".")) || 0,
          partner_commission: parseFloat(String(it.partner_commission).replace(",", ".")) || 0,
          total_bill: parseFloat(String(it.total_bill).replace(",", ".")) || 0,
          icms: parseFloat(String(it.icms).replace(",", ".")) || 0,
          pis: parseFloat(String(it.pis).replace(",", ".")) || 0,
          cofins: parseFloat(String(it.cofins).replace(",", ".")) || 0,
          public_lighting: parseFloat(String(it.public_lighting).replace(",", ".")) || 0,
          others: parseFloat(String(it.others).replace(",", ".")) || 0,
        })),
      };
      const resp = await axiosInstance.post("/simulations/bulk-simulate", payload, { timeout: 120000 });
      const d = resp?.data || {};
      setResults(d.results || []);
      setSimErrors(d.errors || []);
      setSummary(d.summary || null);
      setStep(STEP_RESULTS);
      toast.success(`${(d.results || []).length} simulaçõe(s) geradas.`, { id: toastId });
    } catch (err) {
      toast.error(err?.response?.data?.detail || err?.message || "Erro ao simular", { id: toastId });
    } finally {
      setSimulating(false);
    }
  };

  const downloadSummaryPdf = async () => {
    const ids = results.map((r) => r.simulation_id).filter(Boolean);
    if (ids.length === 0) { toast.error("Nenhuma simulação disponível"); return; }
    const toastId = toast.loading("Gerando PDF resumo…");
    try {
      const resp = await axiosInstance.post(
        "/simulations/bulk-summary-pdf",
        { simulation_ids: ids },
        { responseType: "blob", timeout: 60000 },
      );
      const url = URL.createObjectURL(new Blob([resp.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `Simulacao_Lote_${new Date().toISOString().slice(0, 10)}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success("PDF resumo baixado!", { id: toastId });
    } catch (err) {
      toast.error(err?.response?.data?.detail || err?.message || "Erro ao gerar PDF", { id: toastId });
    }
  };

  return (
    <Layout user={user} onLogout={onLogout}>
      <div className="p-6 max-w-7xl mx-auto space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold flex items-center gap-2 text-slate-800">
              <Sparkles className="w-6 h-6 text-purple-600" />
              Simulador em Lote (IA)
            </h1>
            <p className="text-sm text-slate-500">
              1) Envie as faturas · 2) Revise dados e informe o desconto · 3) Baixe os PDFs individuais.
            </p>
          </div>
          {step !== STEP_UPLOAD && (
            <Button variant="outline" onClick={resetAll} data-testid="bulk-reset-btn">
              Nova análise
            </Button>
          )}
        </div>

        {/* STEP 1 — UPLOAD */}
        {step === STEP_UPLOAD && (
          <Card>
            <CardHeader>
              <CardTitle className="text-base">1. Selecione as faturas (PDF/JPG/PNG · máx. 30 arquivos)</CardTitle>
            </CardHeader>
            <CardContent>
              <input
                ref={fileInputRef} type="file" multiple className="hidden"
                accept="application/pdf,image/jpeg,image/jpg,image/png,image/webp"
                onChange={handleAddFiles} data-testid="bulk-file-input"
              />
              <div className="flex flex-wrap items-center gap-2 mb-4">
                <Button onClick={() => fileInputRef.current?.click()} disabled={extracting}
                  data-testid="bulk-add-files-btn" className="bg-purple-600 hover:bg-purple-700 text-white">
                  <Upload className="w-4 h-4 mr-2" /> Adicionar faturas
                </Button>
                <Button onClick={handleExtract} disabled={extracting || files.length === 0}
                  data-testid="bulk-extract-btn" className="bg-emerald-600 hover:bg-emerald-700 text-white">
                  <Sparkles className="w-4 h-4 mr-2" />
                  {extracting ? "Lendo…" : `Ler ${files.length || ""} fatura(s) com IA`}
                </Button>
              </div>
              {files.length > 0 && (
                <div className="border border-slate-200 rounded-md divide-y" data-testid="bulk-files-list">
                  {files.map((f, i) => (
                    <div key={i} className="flex items-center justify-between px-3 py-2 text-sm">
                      <div className="flex items-center gap-2 truncate">
                        <FileText className="w-4 h-4 text-slate-400" />
                        <span className="truncate">{f.name}</span>
                        <span className="text-xs text-slate-400">{(f.size / 1024).toFixed(0)} KB</span>
                      </div>
                      <Button variant="ghost" size="sm" onClick={() => removeFile(i)} disabled={extracting} data-testid={`bulk-remove-${i}`}>
                        <Trash2 className="w-4 h-4 text-red-500" />
                      </Button>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* STEP 2 — REVIEW & DISCOUNT */}
        {step === STEP_REVIEW && (
          <Card>
            <CardHeader>
              <CardTitle className="text-base flex items-center justify-between">
                <span>2. Revise os dados, escolha a modalidade e informe o desconto (%)</span>
                <div className="flex gap-2 items-center">
                  <Select onValueChange={applyModalityToAll}>
                    <SelectTrigger className="h-8 w-56 text-xs" data-testid="bulk-apply-all-modality-select">
                      <SelectValue placeholder="Modalidade para todas…" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value={GC}>Geração Compartilhada</SelectItem>
                      <SelectItem value={AC}>Autoconsumo Remoto</SelectItem>
                    </SelectContent>
                  </Select>
                  <Button variant="outline" size="sm" onClick={applyDiscountToAll} data-testid="bulk-apply-all-discount-btn">
                    Aplicar % para todas
                  </Button>
                  <Button onClick={handleSimulate} disabled={simulating}
                    data-testid="bulk-simulate-btn" className="bg-emerald-600 hover:bg-emerald-700 text-white">
                    <Calculator className="w-4 h-4 mr-1" />
                    {simulating ? "Simulando…" : "Gerar Simulações"}
                  </Button>
                </div>
              </CardTitle>
            </CardHeader>
            <CardContent>
              {extractErrors.length > 0 && (
                <div className="mb-3 text-xs text-red-700 bg-red-50 border border-red-200 rounded p-2">
                  <strong>{extractErrors.length} arquivo(s) com erro:</strong>{" "}
                  {extractErrors.map((e) => e.filename).join(", ")}
                </div>
              )}
              <div className="overflow-x-auto">
                <Table data-testid="bulk-review-table">
                  <TableHeader>
                    <TableRow>
                      <TableHead>Arquivo</TableHead>
                      <TableHead>Cooperado</TableHead>
                      <TableHead>Distribuidora</TableHead>
                      <TableHead>Modalidade</TableHead>
                      <TableHead className="text-right">Consumo (kWh)</TableHead>
                      <TableHead className="text-right">Tarifa (R$/kWh)</TableHead>
                      <TableHead className="text-right">Fatura (R$)</TableHead>
                      <TableHead className="text-right">Desconto (%)</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {items.map((it, i) => (
                      <TableRow key={i}>
                        <TableCell className="max-w-[180px] truncate text-xs" title={it.filename}>{it.filename}</TableCell>
                        <TableCell>
                          <Input value={it.cooperative_name || ""} onChange={(e) => updateItem(i, "cooperative_name", e.target.value)}
                            className="h-8 text-xs w-48" data-testid={`bulk-cell-name-${i}`} />
                        </TableCell>
                        <TableCell>
                          <Input value={it.distributor || ""} onChange={(e) => updateItem(i, "distributor", e.target.value.toUpperCase())}
                            className="h-8 text-xs w-32" data-testid={`bulk-cell-dist-${i}`} />
                        </TableCell>
                        <TableCell>
                          <Select value={it.compensation_type || GC} onValueChange={(v) => setModality(i, v)}>
                            <SelectTrigger className="h-8 w-44 text-xs" data-testid={`bulk-cell-modality-${i}`}>
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value={GC}>Geração Compartilhada</SelectItem>
                              <SelectItem value={AC}>Autoconsumo Remoto</SelectItem>
                            </SelectContent>
                          </Select>
                        </TableCell>
                        <TableCell>
                          <Input type="number" step="0.01" value={it.consumption} onChange={(e) => updateItem(i, "consumption", e.target.value)}
                            className="h-8 text-xs w-24 text-right" data-testid={`bulk-cell-cons-${i}`} />
                        </TableCell>
                        <TableCell>
                          <Input type="number" step="0.0001" value={it.current_tariff} onChange={(e) => updateItem(i, "current_tariff", e.target.value)}
                            className="h-8 text-xs w-24 text-right" data-testid={`bulk-cell-tariff-${i}`} />
                        </TableCell>
                        <TableCell>
                          <Input type="number" step="0.01" value={it.total_bill} onChange={(e) => updateItem(i, "total_bill", e.target.value)}
                            className="h-8 text-xs w-24 text-right" data-testid={`bulk-cell-bill-${i}`} />
                        </TableCell>
                        <TableCell>
                          <Input type="number" step="0.1" value={it.discount_percentage}
                            onChange={(e) => updateItem(i, "discount_percentage", e.target.value)}
                            className="h-8 text-xs w-20 text-right bg-yellow-50 border-yellow-300 font-semibold"
                            data-testid={`bulk-cell-discount-${i}`} placeholder="obrig." />
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </CardContent>
          </Card>
        )}

        {/* STEP 3 — RESULTS + PDF */}
        {step === STEP_RESULTS && summary && (
          <>
            <Card className="border-2 border-emerald-300 bg-gradient-to-r from-emerald-50 to-teal-50" data-testid="bulk-summary-card">
              <CardHeader>
                <CardTitle className="text-base flex items-center justify-between text-emerald-800">
                  <div className="flex items-center gap-2">
                    <TrendingUp className="w-5 h-5" />
                    Resumo Consolidado ({summary.count} fatura{summary.count === 1 ? "" : "s"})
                  </div>
                  <Button onClick={downloadSummaryPdf} data-testid="bulk-download-summary-pdf-btn"
                    className="bg-blue-600 hover:bg-blue-700 text-white h-8 text-xs">
                    <FileDown className="w-4 h-4 mr-1" /> Baixar PDF Resumo
                  </Button>
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div className="bg-white rounded p-3 border border-emerald-200">
                    <div className="text-[11px] text-emerald-700 uppercase font-semibold">Consumo Total</div>
                    <div className="text-lg font-bold text-slate-800" data-testid="bulk-summary-consumption">{brl(summary.total_consumption)} kWh</div>
                  </div>
                  <div className="bg-white rounded p-3 border border-emerald-200">
                    <div className="text-[11px] text-emerald-700 uppercase font-semibold">Faturas Totais</div>
                    <div className="text-lg font-bold text-slate-800" data-testid="bulk-summary-bill">R$ {brl(summary.total_bill)}</div>
                  </div>
                  <div className="bg-white rounded p-3 border border-emerald-200">
                    <div className="text-[11px] text-emerald-700 uppercase font-semibold">Economia Mensal</div>
                    <div className="text-lg font-bold text-emerald-700" data-testid="bulk-summary-monthly">R$ {brl(summary.total_monthly_savings)}</div>
                  </div>
                  <div className="bg-white rounded p-3 border border-emerald-200">
                    <div className="text-[11px] text-emerald-700 uppercase font-semibold">Economia Anual</div>
                    <div className="text-xl font-extrabold text-emerald-800" data-testid="bulk-summary-annual">R$ {brl(summary.total_annual_savings)}</div>
                  </div>
                </div>
                {Object.keys(summary.by_distributor || {}).length > 0 && (
                  <div className="mt-4">
                    <div className="text-xs uppercase text-emerald-700 font-semibold mb-2">Por Distribuidora</div>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2">
                      {Object.entries(summary.by_distributor).map(([d, v]) => (
                        <div key={d} className="bg-white rounded p-2 border border-emerald-100 text-xs">
                          <div className="flex items-center justify-between">
                            <span className="font-semibold text-slate-700">{d}</span>
                            <span className="text-slate-500">{v.count} fatura(s)</span>
                          </div>
                          <div className="text-slate-600 mt-1">
                            {brl(v.consumption)} kWh · <span className="text-emerald-700 font-semibold">R$ {brl(v.annual_savings)} /ano</span>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>

            {simErrors.length > 0 && (
              <Card className="border-red-300 bg-red-50">
                <CardHeader><CardTitle className="text-sm text-red-700">Erros ao simular ({simErrors.length})</CardTitle></CardHeader>
                <CardContent>
                  <ul className="list-disc ml-5 text-xs text-red-700 space-y-1">
                    {simErrors.map((e, i) => (<li key={i}><strong>{e.filename}</strong>: {e.error}</li>))}
                  </ul>
                </CardContent>
              </Card>
            )}

            <Card>
              <CardHeader><CardTitle className="text-base">Resultado por Fatura</CardTitle></CardHeader>
              <CardContent>
                <div className="overflow-x-auto">
                  <Table data-testid="bulk-results-table">
                    <TableHeader>
                      <TableRow>
                        <TableHead>Arquivo</TableHead>
                        <TableHead>Cooperado</TableHead>
                        <TableHead>Distribuidora</TableHead>
                        <TableHead>Modalidade</TableHead>
                        <TableHead className="text-right">Consumo</TableHead>
                        <TableHead className="text-right">Desconto</TableHead>
                        <TableHead className="text-right">Fatura</TableHead>
                        <TableHead className="text-right">Econ. Mensal</TableHead>
                        <TableHead className="text-right">Econ. Anual</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {results.map((r, i) => (
                        <TableRow key={i}>
                          <TableCell className="max-w-[180px] truncate text-xs" title={r.filename}>{r.filename}</TableCell>
                          <TableCell className="text-xs">{r.cooperative_name || "—"}</TableCell>
                          <TableCell>
                            <span className="text-xs font-semibold bg-blue-50 border border-blue-200 text-blue-800 px-1.5 py-0.5 rounded">
                              {r.distributor || "—"}
                            </span>
                          </TableCell>
                          <TableCell className="text-xs" data-testid={`bulk-result-modality-${i}`}>
                            {isAuto(r.compensation_type) ? "Autoconsumo" : "Compartilhada"}
                          </TableCell>
                          <TableCell className="text-right text-xs">{brl(r.consumption)} kWh</TableCell>
                          <TableCell className="text-right text-xs">{r.discount_percentage}%</TableCell>
                          <TableCell className="text-right text-xs">R$ {brl(r.total_bill)}</TableCell>
                          <TableCell className="text-right text-xs text-emerald-700 font-semibold">R$ {brl(r.monthly_savings)}</TableCell>
                          <TableCell className="text-right text-xs text-emerald-800 font-bold">R$ {brl(r.annual_savings)}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              </CardContent>
            </Card>
          </>
        )}
      </div>
    </Layout>
  );
}
